import type { CustomFunctionUiActionContext } from "src/api/generated/erp/customFunctions/triggers/uiAction/context/customFunctionUiActionContext.ts";

const storageKeyPrefix = "custom-function-ui-action-submission:";

export interface CustomFunctionUiActionIdempotencySession {
	getKey: () => Promise<string>;
	markEdited: () => void;
	clearKey: (idempotencyKey: string) => Promise<void>;
}

export function createCustomFunctionUiActionIdempotencySession(
	principalId: string,
	environmentId: string | null,
	triggerLineageId: string,
	context: CustomFunctionUiActionContext,
): CustomFunctionUiActionIdempotencySession {
	const storageKey = submissionStorageKey(principalId, environmentId, triggerLineageId, context);
	let currentKey: Promise<string> | null = null;
	let replaceBeforeNextSubmission = false;
	return {
		getKey() {
			if (currentKey == null) {
				currentKey = getOrCreateKey(storageKey, replaceBeforeNextSubmission);
				replaceBeforeNextSubmission = false;
			}
			return currentKey;
		},
		markEdited() {
			currentKey = null;
			replaceBeforeNextSubmission = true;
		},
		async clearKey(idempotencyKey) {
			await clearStoredKey(storageKey, idempotencyKey);
			currentKey = null;
		},
	};
}

export async function getCustomFunctionUiActionIdempotencyKey(
	principalId: string,
	environmentId: string | null,
	triggerLineageId: string,
	context: CustomFunctionUiActionContext,
): Promise<string> {
	return getOrCreateKey(submissionStorageKey(principalId, environmentId, triggerLineageId, context), false);
}

export async function clearCustomFunctionUiActionIdempotencyKey(
	principalId: string,
	environmentId: string | null,
	triggerLineageId: string,
	context: CustomFunctionUiActionContext,
	idempotencyKey: string,
): Promise<void> {
	await clearStoredKey(submissionStorageKey(principalId, environmentId, triggerLineageId, context), idempotencyKey);
}

async function getOrCreateKey(storageKey: Promise<string>, replace: boolean): Promise<string> {
	const resolvedStorageKey = await storageKey;
	if (!replace) {
		const existing = sessionStorage.getItem(resolvedStorageKey);
		if (existing != null) return existing;
	}
	const idempotencyKey = crypto.randomUUID();
	sessionStorage.setItem(resolvedStorageKey, idempotencyKey);
	return idempotencyKey;
}

async function clearStoredKey(storageKey: Promise<string>, idempotencyKey: string): Promise<void> {
	const resolvedStorageKey = await storageKey;
	if (sessionStorage.getItem(resolvedStorageKey) === idempotencyKey) {
		sessionStorage.removeItem(resolvedStorageKey);
	}
}

async function submissionStorageKey(
	principalId: string,
	environmentId: string | null,
	triggerLineageId: string,
	context: CustomFunctionUiActionContext,
): Promise<string> {
	const submissionScope = JSON.stringify({ principalId, environmentId, triggerLineageId, context });
	const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(submissionScope));
	const submissionHash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
	return `${storageKeyPrefix}${submissionHash}`;
}
